import React, { useState, useEffect } from 'react';
import { ref, onValue, set, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { toastService } from '../utils/toast';
import { 
  Bell, 
  BellRing,
  Check, 
  X, 
  AlertTriangle, 
  Info, 
  CheckCircle,
  XCircle,
  Users,
  Gamepad2,
  Coins,
  Settings,
  Filter,
  Trash2,
  Plus,
  Send,
  Calendar
} from 'lucide-react';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  category: 'system' | 'user' | 'game' | 'payment' | 'security';
  priority: 'low' | 'medium' | 'high' | 'critical';
  actionUrl?: string;
  metadata?: any;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'info' | 'success' | 'warning' | 'error'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'system' | 'user' | 'game' | 'payment' | 'security'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info' as const,
    category: 'system' as const,
    priority: 'medium' as const
  });
  const auth = getAuth();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = () => {
    try {
      // Mock notifications - valódi implementációban Firebase-ből töltenénk
      const mockNotifications: Notification[] = [
        {
          id: 'notif-1',
          type: 'warning',
          title: 'Rendszer karbantartás',
          message: 'A rendszer karbantartása ma éjjel 02:00-04:00 között lesz ütemezve.',
          timestamp: Date.now() - 300000,
          read: false,
          category: 'system',
          priority: 'high'
        },
        {
          id: 'notif-2',
          type: 'success',
          title: 'Új felhasználó regisztrált',
          message: 'user123@email.com sikeresen regisztrált a rendszerbe.',
          timestamp: Date.now() - 600000,
          read: false,
          category: 'user',
          priority: 'low'
        },
        {
          id: 'notif-3',
          type: 'info',
          title: 'Havi jelentés elkészült',
          message: 'A november havi játék statisztikák elérhetők az analitika menüben.',
          timestamp: Date.now() - 3600000,
          read: true,
          category: 'system',
          priority: 'medium'
        },
        {
          id: 'notif-4',
          type: 'error',
          title: 'Fizetési hiba',
          message: 'Egy felhasználó aranytallér vásárlása sikertelen volt. Azonosító: TXN-123456',
          timestamp: Date.now() - 7200000,
          read: false,
          category: 'payment',
          priority: 'critical'
        },
        {
          id: 'notif-5',
          type: 'warning',
          title: 'Gyanús aktivitás',
          message: 'Szokatlan bejelentkezési kísérlet észlelve az admin fiókból.',
          timestamp: Date.now() - 10800000,
          read: true,
          category: 'security',
          priority: 'high'
        },
        {
          id: 'notif-6',
          type: 'success',
          title: 'Backup elkészült',
          message: 'A napi adatbázis backup sikeresen elkészült.',
          timestamp: Date.now() - 86400000,
          read: true,
          category: 'system',
          priority: 'low'
        }
      ];
      
      setNotifications(mockNotifications);
      setLoading(false);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      setNotifications(notifications.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      ));
      toastService.success('Értesítés olvasottként jelölve');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toastService.error('Hiba történt az értesítés jelölésekor');
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications(notifications.map(notif => ({ ...notif, read: true })));
      toastService.success('Minden értesítés olvasottként jelölve');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toastService.error('Hiba történt az értesítések jelölésekor');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      setNotifications(notifications.filter(notif => notif.id !== notificationId));
      toastService.warning('Értesítés törölve');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toastService.error('Hiba történt az értesítés törlésekor');
    }
  };

  const createNotification = async () => {
    try {
      const notification: Notification = {
        id: `notif-${Date.now()}`,
        ...newNotification,
        timestamp: Date.now(),
        read: false
      };
      
      setNotifications([notification, ...notifications]);
      setShowCreateModal(false);
      setNewNotification({
        title: '',
        message: '',
        type: 'info',
        category: 'system',
        priority: 'medium'
      });
      toastService.success('Új értesítés sikeresen létrehozva');
    } catch (error) {
      console.error('Error creating notification:', error);
      toastService.error('Hiba történt az értesítés létrehozásakor');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 dark:bg-green-900 dark:border-green-700';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900 dark:border-yellow-700';
      case 'error':
        return 'bg-red-50 border-red-200 dark:bg-red-900 dark:border-red-700';
      default:
        return 'bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'user':
        return <Users className="h-4 w-4" />;
      case 'game':
        return <Gamepad2 className="h-4 w-4" />;
      case 'payment':
        return <Coins className="h-4 w-4" />;
      case 'security':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    const matchesType = filter === 'all' || 
                       (filter === 'unread' && !notif.read) ||
                       notif.type === filter;
    const matchesCategory = categoryFilter === 'all' || notif.category === categoryFilter;
    return matchesType && matchesCategory;
  });

  const unreadCount = notifications.filter(notif => !notif.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Értesítések
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                {unreadCount} olvasatlan
              </span>
            )}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Rendszer értesítések és figyelmeztetések kezelése
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
          <button 
            onClick={markAllAsRead}
            className="btn-secondary w-full sm:w-auto"
          >
            <Check className="mr-2 h-4 w-4" />
            Mind olvasott
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Új értesítés
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden típus</option>
                <option value="unread">Olvasatlan</option>
                <option value="info">Információ</option>
                <option value="success">Siker</option>
                <option value="warning">Figyelmeztetés</option>
                <option value="error">Hiba</option>
              </select>
            </div>
            <div className="flex-1">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden kategória</option>
                <option value="system">Rendszer</option>
                <option value="user">Felhasználó</option>
                <option value="game">Játék</option>
                <option value="payment">Fizetés</option>
                <option value="security">Biztonság</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{notifications.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BellRing className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Olvasatlan</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{unreadCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Kritikus</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {notifications.filter(n => n.priority === 'critical').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ma</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {notifications.filter(n => 
                    new Date(n.timestamp).toDateString() === new Date().toDateString()
                  ).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`card border ${getTypeColor(notification.type)} ${
              !notification.read ? 'ring-2 ring-blue-500 ring-opacity-20' : ''
            }`}
          >
            <div className="card-body">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {getTypeIcon(notification.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Új
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getCategoryIcon(notification.category)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(notification.priority)}`}>
                        {notification.priority}
                      </span>
                    </div>
                  </div>
                  
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {notification.message}
                  </p>
                  
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(notification.timestamp).toLocaleString('hu-HU')}
                    </p>
                    
                    <div className="flex space-x-2">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Jelölés olvasottként"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Törlés"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredNotifications.length === 0 && (
          <div className="card">
            <div className="card-body text-center">
              <Bell className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nincsenek értesítések</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Jelenleg nincsenek a kiválasztott szűrőknek megfelelő értesítések.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Notification Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Új Értesítés Létrehozása
              </h3>
              
              <form className="space-y-4">
                <div>
                  <label className="form-label">Cím</label>
                  <input
                    type="text"
                    value={newNotification.title}
                    onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                    className="form-input"
                    placeholder="Értesítés címe"
                  />
                </div>
                
                <div>
                  <label className="form-label">Üzenet</label>
                  <textarea
                    rows={3}
                    value={newNotification.message}
                    onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
                    className="form-input"
                    placeholder="Értesítés szövege"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Típus</label>
                    <select
                      value={newNotification.type}
                      onChange={(e) => setNewNotification({...newNotification, type: e.target.value as any})}
                      className="form-input"
                    >
                      <option value="info">Információ</option>
                      <option value="success">Siker</option>
                      <option value="warning">Figyelmeztetés</option>
                      <option value="error">Hiba</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Kategória</label>
                    <select
                      value={newNotification.category}
                      onChange={(e) => setNewNotification({...newNotification, category: e.target.value as any})}
                      className="form-input"
                    >
                      <option value="system">Rendszer</option>
                      <option value="user">Felhasználó</option>
                      <option value="game">Játék</option>
                      <option value="payment">Fizetés</option>
                      <option value="security">Biztonság</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Prioritás</label>
                    <select
                      value={newNotification.priority}
                      onChange={(e) => setNewNotification({...newNotification, priority: e.target.value as any})}
                      className="form-input"
                    >
                      <option value="low">Alacsony</option>
                      <option value="medium">Közepes</option>
                      <option value="high">Magas</option>
                      <option value="critical">Kritikus</option>
                    </select>
                  </div>
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Mégse
                </button>
                <button
                  onClick={createNotification}
                  className="btn-primary"
                  disabled={!newNotification.title || !newNotification.message}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Létrehozás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications; 